import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield, Lock, Mail, Loader2 } from "lucide-react";

export default function AdminLogin() {
  const { user, loading, login, logout, formatApiError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return <div className="min-h-screen grid place-items-center bg-black text-white/50">Loading…</div>;
  if (user?.is_admin) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = await login(email, password);
      if (!u.is_admin) {
        setErr("This account does not have administrator access.");
        await logout();
      } else {
        navigate("/admin", { replace: true });
      }
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(d?.code ? `${d.message}${d.reason ? ` Reason: ${d.reason}` : ""}` : formatApiError(d) || "Login failed");
    } finally { setBusy(false); }
  };

  const inp = "w-full pl-10 pr-3 py-2.5 rounded-lg bg-black/40 border border-white/10 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[hsl(var(--primary))]";

  return (
    <div className="min-h-screen grid place-items-center bg-[hsl(var(--background))] relative overflow-hidden grain px-4">
      <img src="https://images.unsplash.com/photo-1598550511769-875ef2a01663?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
      <div className="relative z-10 w-full max-w-md card-surface rounded-2xl p-8">
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-2xl grid place-items-center mb-4" style={{ background: "hsl(var(--accent))" }}>
            <Shield size={26} className="text-white" />
          </div>
          <div className="flex items-center justify-center gap-2">
            <span className="font-head text-2xl font-bold" style={{ color: "hsl(var(--accent))" }}>7</span>
            <span className="font-head text-xl font-bold text-white">LEGENDARY</span>
          </div>
          <p className="eyebrow text-white/40 mt-1">Admin Control Panel</p>
        </div>

        <form onSubmit={submit} className="mt-6 space-y-3">
          <div className="relative">
            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="admin@email.com" className={inp} />
          </div>
          <div className="relative">
            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="Password" className={inp} />
          </div>
          {err && <div data-testid="admin-login-error" className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2">{err}</div>}
          <button type="submit" disabled={busy} data-testid="admin-login-submit"
            className="w-full py-3 rounded-lg bg-[hsl(var(--primary))] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60">
            {busy && <Loader2 size={16} className="animate-spin" />} Sign In to Dashboard
          </button>
        </form>
        <a href="/" className="mt-6 block text-center text-xs text-white/40 hover:text-white">← Back to website</a>
      </div>
    </div>
  );
}
